import type { LucideIcon } from 'lucide-react'
import { ArrowRight } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'

/**
 * TaskTile - a large "what do you want to do?" card for a page's top tasks.
 *
 * One tile per task, plain-English title and a one-line why. Big targets and
 * 14px+ text so the next step is obvious at a glance and easy to hit. Lay
 * several out with ``TaskTiles``.
 */
export function TaskTile({
  icon: Icon,
  title,
  description,
  onClick,
  testId,
}: {
  icon: LucideIcon
  title: string
  description: string
  onClick: () => void
  testId?: string
}) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      data-testid={testId}
      className="group h-auto w-full cursor-pointer flex-col items-start justify-start gap-0 whitespace-normal rounded-[12px] border-[0.5px] border-solid border-[var(--rule-heavy)] bg-[var(--bg)] p-4 text-left md:p-5 transition-all duration-200 hover:border-[var(--green)] hover:bg-[var(--bg)] hover:shadow-[0_2px_12px_var(--green-bg-muted)]"
    >
      <span className="mb-3 flex w-full items-start justify-between md:mb-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[var(--green-bg)] text-[var(--green)]">
          <Icon className="h-5 w-5" />
        </span>
        <ArrowRight className="mt-1 h-4 w-4 text-[var(--ink-3)] transition-transform duration-200 group-hover:translate-x-1 group-hover:text-[var(--green)]" />
      </span>
      <span className="mb-1 text-[16px] font-normal text-[var(--ink)]">{title}</span>
      <span className="text-[13px] font-light leading-[1.5] text-[var(--ink-2)]">
        {description}
      </span>
    </Button>
  )
}

/** Responsive row of TaskTiles: one column on phones, up to three on desktop. */
export function TaskTiles({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-8 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">{children}</div>
  )
}
